import React from "react";
import { Button, Typography, Box, useTheme } from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ErrorIcon from "@mui/icons-material/Error";
import InfoIcon from "@mui/icons-material/Info";
import WarningIcon from "@mui/icons-material/Warning";
import { DtDialog } from "./DtDialog";

interface DtStatusDialogProps {
  open: boolean;
  onClose: () => void;
  status?: "success" | "error" | "info" | "warning";
  title?: React.ReactNode;
  message?: React.ReactNode;
  closeLabel?: string;
  isClosable?: boolean;
  maxWidth?: "xs" | "sm" | "md" | "lg" | "xl";
  icon?: React.ReactNode; 
}

const statusIconMap = {
  success: CheckCircleIcon,
  error: ErrorIcon,
  info: InfoIcon,
  warning: WarningIcon,
};

const DtStatusDialog: React.FC<DtStatusDialogProps> = ({
  open,
  onClose,
  status = "info",
  title,
  message,
  closeLabel = "OK",
  maxWidth = "xs",
  isClosable = true,
  icon,
}) => {
  const theme = useTheme();

  const statusColors: Record<string, string> = {
    success: theme.palette.success.main,
    error: theme.palette.error.main,
    info: theme.palette.primary.dark,
    warning: theme.palette.warning.main,
  };

  const DefaultIcon = statusIconMap[status];

  return (
    <DtDialog
      maxWidth={maxWidth}
      open={open}
      onClose={onClose}
      isClosable={isClosable}
      title={
        <Box display="flex" alignItems="center" gap={1}>
          {icon ? (
            icon
          ) : DefaultIcon ? (
            <DefaultIcon
              sx={{
                color: statusColors[status],
                fontSize: 24,
                verticalAlign: "middle",
                opacity: 0.9
              }}
            />
          ) : null}
          <Typography variant="h5" component="span">
            {title || status.charAt(0).toUpperCase() + status.slice(1)}
          </Typography>
        </Box>
      }
      actions={
        isClosable && <Button onClick={onClose} color="primary" variant="contained" size="small">
          {closeLabel}
        </Button>
      }
    >
      {typeof message === "string" ? (
        <Typography variant="body1">
          {message}
        </Typography>
      ) : (
        message
      )}
    </DtDialog>
  );
};

export { DtStatusDialog };